<template>
  <div
    class="form-container"
  >
    <h2 class="page-title-heading">{{ $localize('AddCameraTitle') }}</h2>
    <CameraEditForm
      ref="editForm"
      v-model:camera-name="cameraModel.name"
      v-model:internal-name="cameraModel.internalName"
      v-model:camera-make="cameraModel.make"
      v-model:camera-model="cameraModel.model"
      v-model:camera-notes="cameraModel.notes"
      v-model:camera-coordinate="cameraModel.coordinate"
      :server-errors="serverErrors"
      :validation-rules="validationRules"
    />

    <div class="row">
      <div class="col-xs-12 text-end col-xs-margin-top">
        <VueButton
          :type="VueButtonTypes.primary"
          :loading="saving"
          data-test="add-camera-button"
          @click="onAddCameraClicked"
        >
          {{ $localize('AddCamera') }}
        </VueButton>
      </div>
    </div>
    <ModalDialog
      v-model="showModal"
      :title="$localize('CameraCoordinateCheckModalTitle')"
    >
      <template #default>
        <div>{{ $localize('CameraCoordinateCheckModalMessage') }}</div>
        <br>
        <div>{{ $localize('CameraCoordinateCheckModalQuestion') }}</div>
      </template>
      <template #footer>
        <VueButton
          :type="VueButtonTypes.default"
          data-test="camera-coordinate-modal-cancel-button"
          @click="closeModalDialog"
        >
          {{ $localize('Cancel') }}
        </VueButton>
        <VueButton
          :type="VueButtonTypes.primary"
          data-test="camera-coordinate-modal-yes-button"
          @click="addCamera"
        >
          {{ $localize('Yes') }}
        </VueButton>
      </template>
    </ModalDialog>
  </div>
</template>

<script lang="ts" setup>
  import { ApplicationError } from '@/Services/Clients/applicationError';
  import CameraEditForm from '@/VueComponents/Cameras/CameraDetails/CameraEditForm.vue';
  import { reactive, ref } from 'vue';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
  import VueButton from '@/VueComponents/SharedComponents/Buttons/VueButton.vue';
  import { VueButtonTypes } from '@/VueComponents/SharedComponents/Buttons/Enums/VueButtonTypes';
  import cameraService from '@/Services/cameraService';
  import logger from '@/Utils/logger';
  import { useRouter } from 'vue-router';
  import localStorageHelper from '@/Utils/localStorageHelper';
  import { LocalStorageKeyType } from '@/Types/Enums/localStorageKeyType';
  import ModalDialog from '@/VueComponents/Modals/ModalDialog.vue';
  import usePageLoading from '@/VueCore/utils/usePageLoading';
  import validationRulesService from '@/Services/validationRulesService';

  const contextData = useContextDataStore();
  const router = useRouter();

  const props = defineProps<{
    lat?: number,
    long?: number
  }>();

  const validationRules = ref(null);

  usePageLoading(async () => {
    validationRules.value = await validationRulesService.getValidationRules('camera');
  }, error => console.error('Failed to fetch validation rules:', error), { doNotShowSpinner: true });

  const business = contextData.userData.business;
  const initialCoordinate = getInitialMapCoordinate();

  const showModal = ref(false);

  const cameraModel = reactive({
    name: '',
    internalName: '',
    make: '',
    model: '',
    notes: '',
    coordinate: initialCoordinate
  });

  const editForm = ref(null);
  const saving = ref(false);
  const serverErrors = ref([]);

  defineExpose({
    cameraModel,
    showModal,
    onAddCameraClicked
  });

  async function onAddCameraClicked() {
    if (!(await editForm.value.isValid())) {
      return;
    }

    if ((!props.lat || !props.long) && !cameraCoordinateHasChanged(initialCoordinate, cameraModel.coordinate)) {
      showModalDialog();
      return;
    }

    addCamera();
  }

  function addCamera() {
    closeModalDialog();

    saving.value = true;
    serverErrors.value = [];

    const request = {
      businessId: business.businessId,
      cameraName: cameraModel.name,
      internalName: cameraModel.internalName,
      cameraMake: cameraModel.make,
      cameraModel: cameraModel.model,
      description: cameraModel.notes,
      coordinate: cameraModel.coordinate
    };

    cameraService.addNewCamera(request)
      .then(function () {

        logger.success('CameraAddedSummary');

        localStorageHelper.setSessionValue(LocalStorageKeyType.CamerasPage, String(1));

        router.push('/cameras');
      })
      .catch((error: ApplicationError) => {
        saving.value = false;

        if (error?.errorData?.errorMessages?.length) {
          serverErrors.value = error.errorData.errorMessages;
          return;
        }

        if (!error.isLogged) {
          logger.error('UnhandledError', 'An unexpected error occurred while attempting to add a new camera.', error);
        }
      });
  }

  function cameraCoordinateHasChanged(originalCameraCoordinate, cameraCoordinate) {

    if (originalCameraCoordinate.latitude !== cameraCoordinate.latitude) {
      return true;
    }

    if (originalCameraCoordinate.longitude !== cameraCoordinate.longitude) {
      return true;
    }

    return false;
  }

  function getInitialMapCoordinate() {
    if (props.lat != undefined && props.long != undefined) {
      return {
        longitude: props.long,
        latitude: props.lat
      };
    }

    if (business.coordinate) {
      return business.coordinate;
    }

    return contextData.portalSettings.defaultMapView.coordinate;
  }

  function showModalDialog() {
    showModal.value = true;
  }

  function closeModalDialog() {
    showModal.value = false;
  }
</script>